export const HERO = {
  title: 'PoC Guidelines',
  lead: 'Everything you need to know before starting a Proof of Concept with an external supplier.',
};

export const SECTIONS = [
  { id: 'what', label: 'What is a PoC?' },
  { id: 'when', label: 'When to run one' },
  { id: 'before', label: 'Before you start' },
  { id: 'phases', label: 'The four phases' },
  { id: 'tips', label: 'Tips' },
  { id: 'rules', label: 'Rules' },
] as const;

export const WHAT = {
  title: 'What is a PoC?',
  paragraphs: [
    'A Proof of Concept (PoC) is a short, structured trial of an external supplier’s solution — a startup or technology vendor — to test whether it works for ISS before committing to it. The goal is to validate capabilities we don’t currently have in-house, and to see whether the supplier can actually deliver and support what they’re offering.',
    'A PoC is time-boxed and low-commitment by design. It is not a purchase, not a rollout, and not a promise to the supplier. It’s a controlled way to find out “does this work for us?” before any bigger decision is made.',
  ],
  is: ['Short', 'Structured', 'Time-boxed', 'Low-commitment'],
  isNot: ['A purchase', 'A rollout', 'A promise to the supplier'],
};

export const WHEN = {
  title: 'When should you run a PoC?',
  intro: 'A PoC is the right path when:',
  reasons: [
    'There’s a real business need or challenge you’re trying to solve.',
    'An external supplier may have a solution worth testing.',
    'You want evidence before committing time, money, or a contract.',
  ],
  signposts: [
    {
      title: 'Already know it works?',
      text: 'If you already know the solution works and just want to buy it, that’s a procurement matter, not a PoC.',
    },
    {
      title: 'Not sure it’s worth it?',
      text: 'If you’re not sure whether an idea is worth pursuing at all, the PoC scorecard (early in the process) will help you assess that.',
    },
  ],
};

export const BEFORE = {
  title: 'Before you start',
  intro:
    'The Initiation phase is completed in one session, so it helps to have a few things ready beforehand:',
  items: [
    {
      title: 'A clear description of your idea',
      text: 'What you want to test, why, and what a successful outcome looks like.',
    },
    {
      title: 'The business domain it relates to',
      text: 'E.g. Cleaning, Food, Workplace, IT&D.',
    },
    {
      title: 'Your motivation',
      text: 'The business challenge or use case behind it.',
    },
    {
      title: 'An idea of the value it could create',
      text: 'Cost savings, customer experience, efficiency, sustainability, and so on.',
    },
    {
      title: 'A supplier in mind (if you have one)',
      text: 'Though you can proceed without one.',
    },
  ],
  outro:
    'You don’t need every detail perfected. The tool guides you through each field, and support is available if you’re unsure how to frame your idea.',
};

export type PhaseKey = 'initiation' | 'preparation' | 'running' | 'evaluation';

export type Phase = {
  key: PhaseKey;
  name: string;
  summary: string;
  body: string;
  note?: string;
  checks?: { name: string; text: string; mandatory?: boolean }[];
  checksOutro?: string;
  chips?: string[];
  outcomes?: { answer: 'No' | 'Yes'; text: string }[];
};

export const PHASES_INTRO = {
  title: 'The four phases',
  intro:
    'Every PoC moves through four phases, in order. You can’t skip ahead — each phase builds on the one before it.',
};

export const PHASES: Phase[] = [
  {
    key: 'initiation',
    name: 'Initiation',
    summary: 'Describe and justify your idea',
    body: 'You describe and justify your idea. This phase captures the business justification, then asks you to complete a scorecard — a set of yes/no questions that produces a viability score and a recommendation. You’ll see your result, confirm it, and choose whether you’d like facilitation support. Completing this phase submits your PoC for its journey.',
    note: 'The scorecard is advisory — a low score doesn’t block you, but it’s a strong signal to refine your idea before going further.',
  },
  {
    key: 'preparation',
    name: 'Preparation',
    summary: 'Get ready to run, under the right conditions',
    body: 'This is where you get your PoC ready to run, under the right conditions. You’ll define your success criteria (how you’ll judge whether the PoC worked), specify where it runs, and work through a series of compliance checks:',
    checks: [
      { name: 'NDA', text: 'Whether a confidentiality agreement is needed with the supplier.' },
      { name: 'Data Protection', text: 'Whether personal data is involved, and what that requires.' },
      { name: 'IT Security', text: 'A mandatory risk assessment of the solution.', mandatory: true },
      { name: 'Legal', text: 'The right agreement, signed with legal support.' },
      { name: 'Procurement', text: 'Confirming you’re staying within PoC scope.' },
    ],
    checksOutro:
      'Each step tells you exactly what’s required based on your answers. The IT Security assessment must be completed before this phase can finish.',
  },
  {
    key: 'running',
    name: 'Running',
    summary: 'Your PoC is live',
    body: 'Your PoC is live. This phase gives you the templates and guidance to run it well — milestone and launch communication plans, hypercare support, feedback collection, and evaluation preparation. You’ll also set how long the PoC runs. When that period ends, the platform automatically prompts you to wrap up and move to evaluation.',
    chips: [
      'Milestone plan',
      'Launch communication',
      'Hypercare support',
      'Feedback collection',
      'Evaluation preparation',
    ],
    note: 'There’s no strict order here — everything’s available to you at once.',
  },
  {
    key: 'evaluation',
    name: 'Evaluation',
    summary: 'Assess the results and decide',
    body: 'You assess the results. For each success criterion you defined earlier, you record whether it was met and how you measured it. You’ll upload your evaluation report, and then make the key decision: was the PoC successful, and do you want to proceed?',
    outcomes: [
      { answer: 'No', text: 'The PoC is archived, with your reasoning captured.' },
      {
        answer: 'Yes',
        text: 'The platform points you to the right next step: an IT Demand for IT solutions, or a Business Case for non-digital ones.',
      },
    ],
  },
];

export const TIPS = {
  title: 'Tips for a good PoC',
  items: [
    {
      title: 'Write clear success criteria.',
      text: '“It worked well” isn’t measurable. “Reduced processing time by 20%” is. Good criteria make the evaluation honest and easy.',
    },
    {
      title: 'Be realistic about scope.',
      text: 'A PoC proves one thing well. Trying to test everything at once usually proves nothing.',
    },
    {
      title: 'Don’t over-commit.',
      text: 'A PoC is a test, not a contract. Keep commercial commitments out of it — that’s what procurement is for, later.',
    },
    {
      title: 'Ask for help early.',
      text: 'If you’re unsure how to describe or shape your idea, request facilitation support at the start rather than struggling alone.',
    },
    {
      title: 'Keep your supplier informed, but protected.',
      text: 'Sign the NDA before sharing anything confidential.',
    },
  ],
};

export const RULES = {
  title: 'Rules and obligations',
  intro:
    'The following are mandatory, not optional. They exist to keep PoCs safe, compliant, and low-risk for ISS.',
  items: [
    'The process must be followed in order. A PoC cannot enter Preparation until Initiation is submitted, cannot run until Preparation is complete, and cannot close without a recorded outcome.',
    'An NDA must be signed before any confidential information is shared with a supplier.',
    'The IT Security assessment is mandatory and must be completed before Preparation can be finished. A high-risk result requires engagement with GICSS Cyber Risk.',
    'Data protection obligations must be met. Where personal data is involved, the required agreements or assessments (DPA, PIA, TIA) must be actioned — the tool will tell you which apply.',
    'No commercial commitments may be made beyond the scope of the PoC. If the PoC succeeds and you want to proceed, a formal sourcing request follows through procurement.',
    'A PoC must have a recorded outcome. Successful or not, every PoC ends with a documented decision and reasoning.',
  ],
};

export const CLOSING =
  'If you’re unsure about anything in this process, reach out to the Global Digital Innovation team before you begin.';
